import React, { Suspense, lazy } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes as RouterRoutes,
  useParams,
} from "react-router-dom";
import ThemedToastContainer from "../components/ThemedToastContainer";
import BackdropLoading from "../components/BackdropLoading";

// As telas do dia a dia entram no bundle principal; todo o resto carrega
// quando a rota abre (React.lazy). Importar as ~60 páginas de uma vez punha
// Flow Builder (@xyflow), gráficos (recharts) e o resto num único arquivo de
// 4,4 MB que TODA tela — inclusive o login — pagava para baixar e compilar
// antes do primeiro desenho. Medido em 11/09/2026 (build de produção, CPU 4x,
// Fast 4G): 1,1 s de tela branca só de script, antes de qualquer requisição.
import Tickets from "../pages/Tickets/";
import Login from "../pages/Login/";

const Dashboard = lazy(() => import("../pages/Dashboard/"));
const Signup = lazy(() => import("../pages/Signup/"));
const AcessoPorEmail = lazy(() => import("../pages/AcessoPorEmail"));
const Connections = lazy(() => import("../pages/Connections/"));
const Settings = lazy(() => import("../pages/Settings/"));
const Users = lazy(() => import("../pages/Users"));
const Contacts = lazy(() => import("../pages/Contacts/"));
const QuickAnswers = lazy(() => import("../pages/QuickAnswers/"));
const Queues = lazy(() => import("../pages/Queues/"));
const QueueDetails = lazy(() => import("../pages/QueueDetails"));
const Tags = lazy(() => import("../pages/Tags/"));
const LinksDeRedirecionamento = lazy(() => import("../pages/LinksDeRedirecionamento/"));
const Chamadas = lazy(() => import("../pages/Chamadas"));
const Gravacoes = lazy(() => import("../pages/Gravacoes"));
const Teams = lazy(() => import("../pages/Teams"));
const Webhooks = lazy(() => import("../pages/Webhooks"));
const ZernioContas = lazy(() => import("../pages/Zernio"));
const ModelosWhatsapp = lazy(() => import("../pages/ModelosWhatsapp"));
const ModeloWhatsappDetails = lazy(() => import("../pages/ModeloWhatsappDetails"));
const ZernioPublicacoes = lazy(() => import("../pages/Zernio/Publicacoes"));
const ZernioComentarios = lazy(() => import("../pages/Zernio/Comentarios"));
const ZernioMencoes = lazy(() => import("../pages/Zernio/Mencoes"));
const ZernioAvaliacoes = lazy(() => import("../pages/Zernio/Avaliacoes"));
const ZernioAnalises = lazy(() => import("../pages/Zernio/Analises"));
const ZernioAnuncios = lazy(() => import("../pages/Zernio/Anuncios"));
const ZernioConfiguracao = lazy(() => import("../pages/Zernio/Configuracao"));
const Farmax = lazy(() => import("../pages/Farmax"));
const FarmaxConexao = lazy(() => import("../pages/Farmax/Conexao"));
const FarmaxProdutos = lazy(() => import("../pages/Farmax/Produtos"));
const FarmaxPedidos = lazy(() => import("../pages/Farmax/Pedidos"));
const ScheduledMessages = lazy(() => import("../pages/ScheduledMessages"));
const MediaLibrary = lazy(() => import("../pages/MediaLibrary"));
const Products = lazy(() => import("../pages/Products"));
const ProductDetails = lazy(() => import("../pages/ProductDetails"));
const CategoriaDoCatalogo = lazy(() => import("../pages/CategoriaDoCatalogo"));
const ModelosDeCatalogo = lazy(() => import("../pages/ModelosDeCatalogo"));
const Locacoes = lazy(() => import("../pages/Locacoes"));
// Endereços antigos de orçamento e pedido: só redirecionam para /vendas.
const RedirecionarOrcamento = lazy(() =>
  import("../pages/Vendas/Redirecionar").then((m) => ({
    default: m.RedirecionarOrcamento,
  }))
);
const RedirecionarPedido = lazy(() =>
  import("../pages/Vendas/Redirecionar").then((m) => ({
    default: m.RedirecionarPedido,
  }))
);
const Vendas = lazy(() => import("../pages/Vendas"));
const VendaDetalhe = lazy(() => import("../pages/Vendas/Detalhe"));
const OrcamentoPublico = lazy(() => import("../pages/Quotes/Publico"));
const CarrinhoPublico = lazy(() => import("../pages/Carrinho/Publico"));
const LojaPublica = lazy(() =>
  import("../pages/Loja/Vitrine").then((m) => ({
    default: () => <m.default tipo="loja" />,
  }))
);
const Propostas = lazy(() => import("../pages/Propostas"));
const PropostaDetalhe = lazy(() => import("../pages/PropostaDetalhe"));
const DocumentoPublico = lazy(() => import("../pages/Propostas/Publico"));
const WebchatPublico = lazy(() => import("../pages/Webchat/Publico"));
const WebchatEntrar = lazy(() =>
  import("../pages/Webchat/Publico").then((m) => ({ default: m.WebchatEntrar }))
);
const ModelosDeDocumento = lazy(() => import("../pages/ModelosDeDocumento"));
const FollowUps = lazy(() => import("../pages/FollowUps"));
const ReguaDetalhe = lazy(() => import("../pages/ReguaDetalhe"));
const Campaigns = lazy(() => import("../pages/Campaigns/"));
const CampaignDetails = lazy(() => import("../pages/CampaignDetails"));
const CampaignEditor = lazy(() => import("../pages/CampaignEditor"));
const NumeroOficial = lazy(() => import("../pages/NumeroOficial"));
const ConnectionDetails = lazy(() => import("../pages/ConnectionDetails"));
const UserDetails = lazy(() => import("../pages/UserDetails"));
const InstagramRetorno = lazy(() => import("../pages/InstagramRetorno"));
const PoliticaDePrivacidade = lazy(() =>
  import("../pages/PoliticaDePrivacidade")
);
const Maturador = lazy(() => import("../pages/Maturador"));
const MaturadorDetails = lazy(() => import("../pages/MaturadorDetails"));
const Kanban = lazy(() => import("../pages/Kanban"));
const KanbanCardDetails = lazy(() => import("../pages/KanbanCardDetails"));
const InternalChat = lazy(() => import("../pages/InternalChat"));
const Flows = lazy(() => import("../pages/Flows"));
const BibliotecaDeFluxos = lazy(() => import("../pages/Flows/Biblioteca"));
const FlowTemplates = lazy(() => import("../pages/FlowTemplates"));
const StatusPosts = lazy(() => import("../pages/Status"));
const StatusDetalhe = lazy(() => import("../pages/StatusDetalhe"));
const FlowBuilder = lazy(() => import("../pages/FlowBuilder"));
const Reports = lazy(() => import("../pages/Reports"));
const Plans = lazy(() => import("../pages/Plans"));
const Companies = lazy(() => import("../pages/Companies"));
const Onboarding = lazy(() => import("../pages/Onboarding"));
const FormulariosDeImplantacao = lazy(() => import("../pages/FormulariosDeImplantacao"));
const EditorDoFormulario = lazy(() => import("../pages/FormulariosDeImplantacao/Editor"));
const ImplantacaoPublica = lazy(() => import("../pages/ImplantacaoPublica"));
const Invoices = lazy(() => import("../pages/Invoices"));
const Billing = lazy(() => import("../pages/Billing"));
const Revenda = lazy(() => import("../pages/Revenda"));
const Rede = lazy(() => import("../pages/Rede"));
const RedeFilial = lazy(() => import("../pages/RedeFilial"));
const RedeAtendimentos = lazy(() => import("../pages/RedeAtendimentos"));
const RedeMonitor = lazy(() => import("../pages/RedeMonitor"));
const RedeUsuarios = lazy(() => import("../pages/RedeUsuarios"));
const RedePadronizar = lazy(() => import("../pages/RedePadronizar"));
const Redes = lazy(() => import("../pages/Redes"));
const QueueIntegrations = lazy(() => import("../pages/QueueIntegrations"));
const MessagesApi = lazy(() => import("../pages/MessagesApi"));
const Helps = lazy(() => import("../pages/Helps"));
const CampaignsPhrases = lazy(() => import("../pages/CampaignsPhrases"));
const CampaignsConfig = lazy(() => import("../pages/CampaignsConfig"));
const GlobalConfig = lazy(() => import("../pages/GlobalConfig"));
const EstilosVisuais = lazy(() => import("../pages/EstilosVisuais"));
const EditorDeEstilo = lazy(() => import("../pages/EstilosVisuais/Editor"));
const CompanyDetails = lazy(() => import("../pages/CompanyDetails"));
const Modules = lazy(() => import("../pages/Modules"));
const Roles = lazy(() => import("../pages/Roles"));
const AiHub = lazy(() => import("../pages/AiHub"));
const OperationalPanel = lazy(() => import("../pages/OperationalPanel"));
const MonitorDeAtendimentos = lazy(() => import("../pages/MonitorDeAtendimentos"));
const ContactTimeline = lazy(() => import("../pages/ContactTimeline"));
const ContactDetails = lazy(() => import("../pages/ContactDetails"));
const Organizations = lazy(() => import("../pages/Organizations"));
const TicketReplay = lazy(() => import("../pages/TicketReplay"));
const SaasFinance = lazy(() => import("../pages/SaasFinance"));
const Commercial = lazy(() => import("../pages/Commercial"));
const Announcements = lazy(() => import("../pages/Announcements"));
const ContactLists = lazy(() => import("../pages/ContactLists"));
import { AuthProvider } from "../context/Auth/AuthContext";
import { ThemeProvider } from "../context/DarkMode";
import { WhiteLabelProvider } from "../context/WhiteLabel";
import { PrivateRoutes, PublicRoute } from "./Route";

// Link antigo de /companies/:id/subscription cai na aba da página nova.
const ParaAbaAssinatura = () => {
  const { companyId } = useParams();
  return <Navigate replace to={`/companies/${companyId}?aba=assinatura`} />;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <WhiteLabelProvider>
          {/* O fallback cobre o instante entre clicar na rota e o pedaço
              dela chegar — o mesmo backdrop que as telas já usam. */}
          <Suspense fallback={<BackdropLoading />}>
          <RouterRoutes>
            {/* Aberta a quem não tem conta: é o endereço que a Meta exige
                para o aplicativo sair do modo de desenvolvimento, e quem lê é
                o cliente final. Fora do PublicRoute pelo mesmo motivo das
                outras públicas — ele mandaria para o início quem estivesse
                logado, e a página vale para os dois. */}
            <Route
              path="/politica-de-privacidade"
              element={<PoliticaDePrivacidade />}
            />
            {/* Sem guarda nenhuma: quem abre é o cliente, que não tem login —
                e o PublicRoute mandaria para o início quem estivesse logado,
                que é justamente quem confere o link antes de enviar. */}
            <Route path="/orcamento/:token" element={<OrcamentoPublico />} />
            {/* O catálogo que o cliente monta sozinho. Mesma regra do
                orçamento: o token é a credencial, quem abre não tem login. */}
            <Route path="/carrinho/:token" element={<CarrinhoPublico />} />
            {/* A loja virtual: o mesmo catálogo, num endereço fixo da
                empresa, aberto para quem não tem link de conversa. */}
            <Route path="/loja/:slug" element={<LojaPublica />} />
            {/* Proposta e contrato: o mesmo motivo do orçamento acima — o
                link vai para o cliente, que não tem login. O token não diz
                qual documento é, então quem separa os dois é a rota. */}
            <Route
              path="/p/:token"
              element={<DocumentoPublico tipo="proposal" />}
            />
            <Route
              path="/c/:token"
              element={<DocumentoPublico tipo="contract" />}
            />
            {/* Webchat do visitante: link público, widget (?embed=1) e link
                por atendimento. Mesma regra das públicas acima. */}
            <Route path="/w/:token" element={<WebchatPublico />} />
            <Route path="/w/entrar/:linkToken" element={<WebchatEntrar />} />
            {/* Formulário de implantação: quem abre é o cliente, que ainda
                nem tem conta. Mesma regra das públicas acima. */}
            <Route path="/implantacao/:token" element={<ImplantacaoPublica />} />
            {/* Links que chegam por e-mail. Fora do PublicRoute: quem abre o
                link de confirmação costuma estar logado em outra aba, e o
                PublicRoute o mandaria para o início sem confirmar nada. */}
            <Route path="/esqueci-senha" element={<AcessoPorEmail modo="esqueci" />} />
            <Route path="/redefinir-senha" element={<AcessoPorEmail modo="redefinir" />} />
            <Route path="/confirmar-email" element={<AcessoPorEmail modo="confirmar" />} />
            <Route element={<PublicRoute />}>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
            </Route>
            <Route element={<PrivateRoutes />}>
              <Route index element={<Dashboard />} />
              <Route path="/tickets/:ticketId?" element={<Tickets />} />
              <Route path="/connections" element={<Connections />} />
              {/* Rota fixa antes da com :whatsappId, senão "instagram"
                  seria lido como o id de uma conexão. */}
              <Route
                path="/connections/instagram/retorno"
                element={<InstagramRetorno />}
              />
              <Route
                path="/connections/:whatsappId/oficial"
                element={<NumeroOficial />}
              />
              <Route
                path="/connections/:whatsappId"
                element={<ConnectionDetails />}
              />
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/contacts/:contactId" element={<ContactDetails />} />
              <Route
                path="/contacts/:contactId/timeline"
                element={<ContactTimeline />}
              />
              <Route
                path="/tickets/:ticketId/replay"
                element={<TicketReplay />}
              />
              <Route path="/users" element={<Users />} />
              <Route path="/users/:userId" element={<UserDetails />} />
              <Route path="/roles" element={<Roles />} />
              <Route path="/ai" element={<AiHub />} />
              <Route path="/quickAnswers" element={<QuickAnswers />} />
              <Route path="/Settings" element={<Settings />} />
              <Route path="/Queues" element={<Queues />} />
              <Route path="/Queues/novo" element={<QueueDetails />} />
              <Route path="/Queues/:queueId" element={<QueueDetails />} />
              {/* Departamentos virou o nome da tela de filas; favorito antigo
                  da tela removida cai aqui em vez de num 404. */}
              <Route
                path="/departments"
                element={<Navigate replace to="/Queues" />}
              />
              <Route path="/tags" element={<Tags />} />
              <Route path="/links-de-redirecionamento" element={<LinksDeRedirecionamento />} />
              <Route path="/chamadas" element={<Chamadas />} />
              <Route path="/gravacoes" element={<Gravacoes />} />
              <Route path="/organizations" element={<Organizations />} />
              <Route path="/teams" element={<Teams />} />
              <Route path="/webhooks" element={<Webhooks />} />
              {/* Cada assunto da Zernio é uma tela, com item próprio no menu:
                  gerenciar publicação e responder comentário são trabalhos
                  diferentes, e empilhá-los em abas escondia metade deles. */}
              <Route path="/modelos-whatsapp" element={<ModelosWhatsapp />} />
              <Route path="/modelos-whatsapp/novo" element={<ModeloWhatsappDetails />} />
              <Route path="/modelos-whatsapp/:id" element={<ModeloWhatsappDetails />} />
              <Route path="/zernio" element={<ZernioContas />} />
              <Route
                path="/zernio/publicacoes"
                element={<ZernioPublicacoes />}
              />
              <Route
                path="/zernio/comentarios"
                element={<ZernioComentarios />}
              />
              <Route path="/zernio/mencoes" element={<ZernioMencoes />} />
              <Route path="/zernio/avaliacoes" element={<ZernioAvaliacoes />} />
              <Route path="/zernio/analises" element={<ZernioAnalises />} />
              <Route path="/zernio/anuncios" element={<ZernioAnuncios />} />
              <Route
                path="/zernio/configuracao"
                element={<ZernioConfiguracao />}
              />
              <Route path="/farmax" element={<Farmax />} />
              <Route path="/farmax/conexao" element={<FarmaxConexao />} />
              <Route path="/farmax/produtos" element={<FarmaxProdutos />} />
              <Route path="/farmax/pedidos" element={<FarmaxPedidos />} />
              <Route path="/queue-integrations" element={<QueueIntegrations />} />
              <Route path="/messages-api" element={<MessagesApi />} />
              <Route path="/helps" element={<Helps />} />
              <Route path="/campaigns-phrases" element={<CampaignsPhrases />} />
              <Route path="/campaigns-config" element={<CampaignsConfig />} />
              <Route path="/global-config" element={<GlobalConfig />} />
              <Route path="/estilos-visuais" element={<EstilosVisuais />} />
              <Route path="/estilos-visuais/:id" element={<EditorDeEstilo />} />
              <Route path="/scheduled-messages" element={<ScheduledMessages />} />
              <Route path="/media-library" element={<MediaLibrary />} />
              <Route path="/products" element={<Products />} />
              <Route
                path="/products/categorias/:categoryId"
                element={<CategoriaDoCatalogo />}
              />
              <Route path="/products/modelos" element={<ModelosDeCatalogo />} />
              <Route path="/products/novo" element={<ProductDetails />} />
              <Route path="/products/:productId" element={<ProductDetails />} />
              <Route path="/locacoes" element={<Locacoes />} />
              <Route path="/vendas" element={<Vendas />} />
              <Route path="/vendas/:saleId" element={<VendaDetalhe />} />
              <Route path="/quotes" element={<RedirecionarOrcamento />} />
              <Route path="/pedidos" element={<RedirecionarPedido />} />
              <Route path="/propostas" element={<Propostas />} />
              <Route
                path="/propostas/contratos/novo"
                element={<PropostaDetalhe tipo="contract" />}
              />
              <Route
                path="/propostas/contratos/:id"
                element={<PropostaDetalhe tipo="contract" />}
              />
              <Route
                path="/propostas/:id"
                element={<PropostaDetalhe tipo="proposal" />}
              />
              <Route
                path="/modelos-de-documento"
                element={<ModelosDeDocumento />}
              />
              <Route path="/follow-ups" element={<FollowUps />} />
              <Route path="/follow-ups/novo" element={<ReguaDetalhe />} />
              <Route path="/follow-ups/:id" element={<ReguaDetalhe />} />
              <Route path="/campaigns" element={<Campaigns />} />
              <Route path="/campaigns/new" element={<CampaignEditor />} />
              <Route
                path="/campaigns/:campaignId/edit"
                element={<CampaignEditor />}
              />
              <Route
                path="/campaigns/:campaignId"
                element={<CampaignDetails />}
              />
              <Route path="/maturacao" element={<Maturador />} />
              <Route path="/maturacao/:poolId" element={<MaturadorDetails />} />
              {/* O Kanban é o quadro único. Os endereços das telas que ele
                  substituiu continuam valendo — link salvo, favorito — e
                  levam para cá. */}
              <Route path="/kanban" element={<Kanban />} />
              <Route path="/kanban/cards/:cardId" element={<KanbanCardDetails />} />
              <Route
                path="/quadros"
                element={<Navigate replace to="/kanban" />}
              />
              <Route
                path="/crm/leads"
                element={<Navigate replace to="/kanban" />}
              />
              <Route
                path="/crm/funnel"
                element={<Navigate replace to="/kanban" />}
              />
              <Route path="/boards" element={<Navigate replace to="/kanban" />} />
              <Route path="/internal-chat" element={<InternalChat />} />
              <Route path="/flows" element={<Flows />} />
              <Route path="/flows/modelos" element={<BibliotecaDeFluxos />} />
              <Route path="/status-posts" element={<StatusPosts />} />
              <Route path="/status-posts/novo" element={<StatusDetalhe />} />
              <Route path="/status-posts/:id" element={<StatusDetalhe />} />
              <Route path="/flows/:flowId/edit" element={<FlowBuilder />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/operational-panel" element={<OperationalPanel />} />
              <Route path="/monitor" element={<MonitorDeAtendimentos />} />
              <Route path="/saas/finance" element={<SaasFinance />} />
              <Route path="/saas/commercial" element={<Commercial />} />
              <Route path="/saas/redes" element={<Redes />} />
              <Route path="/plans" element={<Plans />} />
              <Route path="/modules" element={<Modules />} />
              <Route path="/flow-templates" element={<FlowTemplates />} />
              <Route path="/companies" element={<Companies />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/onboarding-forms" element={<FormulariosDeImplantacao />} />
              <Route path="/onboarding-forms/:formId" element={<EditorDoFormulario />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/financeiro" element={<Billing />} />
              {/* Painel da revenda: as telas de implantação em modo revenda. */}
              <Route path="/revenda" element={<Revenda />} />
              {/* Painel da rede: a sede vê os números das filiais. */}
              <Route path="/rede" element={<Rede />} />
              <Route path="/rede/padronizar" element={<RedePadronizar />} />
              <Route path="/rede/monitor" element={<RedeMonitor />} />
              <Route path="/rede/atendimentos" element={<RedeAtendimentos />} />
              <Route path="/rede/filiais/:companyId" element={<RedeFilial />} />
              <Route path="/rede/filiais/:companyId/atendimentos" element={<RedeAtendimentos />} />
              <Route path="/rede/filiais/:companyId/usuarios" element={<RedeUsuarios />} />
              <Route path="/rede/filiais/:companyId/atendimentos/:ticketId" element={<TicketReplay />} />
              <Route path="/revenda/implantar" element={<Onboarding revenda />} />
              <Route path="/revenda/formularios" element={<FormulariosDeImplantacao revenda />} />
              <Route path="/companies/:companyId" element={<CompanyDetails />} />
              {/* A assinatura virou aba da página da empresa. */}
              <Route
                path="/companies/:companyId/subscription"
                element={<ParaAbaAssinatura />}
              />
              <Route path="/announcements" element={<Announcements />} />
              <Route path="/contact-lists" element={<ContactLists />} />
              {/* No router 5 o <Switch> caía no layout mesmo sem rota casada;
                  no 7 sobraria tela em branco. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </RouterRoutes>
          </Suspense>
          <ThemedToastContainer />
          </WhiteLabelProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
